import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminControlCenter, type ControlWindow } from "@/features/admin";
import { useBackOfficeAccess } from "@/features/auth/back-office-access";
import { cn } from "@/lib/utils";
import {
  ArrowsInIcon,
  ArrowsOutIcon,
  PauseIcon,
  PlayIcon,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { AdminPageLayout } from "../layout";
import { CallOutcomes } from "./components/call-outcomes";
import { Histogram, Origins, TopTenants } from "./components/distributions";
import { ago } from "./components/format";
import { HealthBanner } from "./components/health-banner";
import { Kpis } from "./components/kpis";
import { EventFeed, LiveSessions } from "./components/live-activity";
import { Outcomes } from "./components/outcomes";
import { PeakHours } from "./components/peak-hours";
import { TechPanel } from "./components/tech-panel";
import { TrafficChart } from "./components/traffic-chart";
import { WorldMap } from "./components/world-map";

const WINDOWS: { key: ControlWindow; label: string }[] = [
  { key: "today", label: "Aujourd'hui" },
  { key: 1, label: "1 h" },
  { key: 6, label: "6 h" },
  { key: 24, label: "24 h" },
  { key: 24 * 7, label: "7 j" },
  { key: 24 * 30, label: "30 j" },
];

/** A clock for the timers on screen — one interval for the page, not one per row. */
function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function useFullscreen() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.();
  };
  return { on, toggle };
}

/**
 * The control centre: the platform at a glance, meant to be left open.
 *
 * Top to bottom it answers, in order: is it healthy (the verdict and why), how much is
 * going on (the figures against the previous window), what is happening right now — side
 * by side, one third each: where it comes from (countries, channels, languages), what just
 * ended or broke, who is on the line — then traffic, what the calls achieved (bookings,
 * transfers), the map, how it went, and when the phones ring (peak hours over four weeks,
 * the one block that ignores the window). Every block reads the same payload
 * (`GET /control-center`), polled every 10s.
 */
export function AdminControlCenterPage() {
  const [span, setSpan] = useState<ControlWindow>(24);
  const [live, setLive] = useState(true);
  const { data, isLoading, isFetching, dataUpdatedAt } = useAdminControlCenter(
    span,
    live,
  );
  const now = useNow();
  const fullscreen = useFullscreen();
  // The technical panel (latency, tool errors, external APIs, the test call)
  // and its links are for engineers: the read-only account (sales) has none of
  // those pages.
  const technical = useBackOfficeAccess() === "admin";

  const liveCountries = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of data?.live.sessions ?? []) {
      if (s.country_code) map.set(s.country_code, (map.get(s.country_code) ?? 0) + 1);
    }
    return map;
  }, [data]);

  return (
    <AdminPageLayout
      title="Centre de contrôle"
      description="L'état de santé de la plateforme, en direct, sur l'ensemble des tenants"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
            <span
              className={cn(
                "size-2 rounded-full transition-colors",
                !live
                  ? "bg-muted-foreground/40"
                  : isFetching
                    ? "bg-info animate-pulse"
                    : "bg-success",
              )}
            />
            {live ? "En direct" : "En pause"}
            {dataUpdatedAt ? ` · ${ago(dataUpdatedAt, now)}` : ""}
          </div>
          <div className="flex rounded-md border p-0.5 text-xs">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => setSpan(w.key)}
                className={cn(
                  "rounded px-2.5 py-1 transition-colors",
                  span === w.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setLive((v) => !v)}
            title={live ? "Mettre en pause" : "Reprendre le direct"}
          >
            {live ? <PauseIcon /> : <PlayIcon />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fullscreen.toggle}
            title="Plein écran"
          >
            {fullscreen.on ? <ArrowsInIcon /> : <ArrowsOutIcon />}
          </Button>
        </div>
      }
    >
      {isLoading || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-xl" />
          <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-[420px] w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          <HealthBanner health={data.health} live={data.live} />
          <Kpis data={data} />

          <div className="grid gap-4 xl:grid-cols-3">
            <Origins data={data} />
            <EventFeed feed={data.feed} now={now} />
            <LiveSessions live={data.live} now={now} />
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <TrafficChart data={data} />
            </div>
            <Outcomes totals={data.totals} />
          </div>

          {technical ? (
            <div className="grid gap-4 xl:grid-cols-3">
              <div className="xl:col-span-2">
                <CallOutcomes data={data} />
              </div>
              <TechPanel data={data} />
            </div>
          ) : (
            <CallOutcomes data={data} />
          )}

          <div className="grid gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <WorldMap countries={data.countries} liveCountries={liveCountries} />
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
              <Histogram
                title="Échanges par conversation"
                description="Nombre de fois où le client a pris la parole"
                buckets={data.exchanges_histogram}
                color="var(--chart-2)"
                unit="tours de parole"
              />
              <Histogram
                title="Durée des appels"
                description="Temps de parole de l'appelant, appels terminés"
                buckets={data.duration_histogram}
                color="var(--chart-1)"
                unit="durée"
              />
            </div>
          </div>

          <PeakHours peak={data.peak_hours} />

          <TopTenants data={data} />
        </div>
      )}
    </AdminPageLayout>
  );
}
